import type { FC, HTMLAttributes, Ref } from 'react'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import styles from './date-picker.module.css'

interface BaseDatePickerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  label?: string
  value?: Dayjs | null
  defaultValue?: Dayjs | null
  onChange?: (value: Dayjs | null) => void
  /** Called when the user commits the value (on blur/enter) */
  onAccept?: (value: Dayjs | null) => void
  disabled?: boolean
  disableFuture?: boolean
  inputRef?: Ref<HTMLInputElement>
  /** Ignored — kept for API compat */
  format?: string
  /** Ignored */
  disableHighlightToday?: boolean
  shouldDisableDate?: (date: Dayjs) => boolean
  shouldDisableYear?: (date: Dayjs) => boolean
  shouldDisableTime?: (date: Dayjs) => boolean
  /** MUI sx compat — ignored, use style/className */
  sx?: unknown
}


function createDatePicker(type: 'date' | 'time' | 'datetime-local', toNative: (d: Dayjs) => string, fromNative: (s: string) => Dayjs) {
  const Component: FC<BaseDatePickerProps> = ({
    label,
    value,
    defaultValue,
    onChange,
    onAccept,
    disabled,
    disableFuture,
    inputRef,
    format: _format,
    disableHighlightToday: _,
    shouldDisableDate: _shouldDisableDate,
    shouldDisableYear: _shouldDisableYear,
    shouldDisableTime: _shouldDisableTime,
    sx: _sx,
    style,
    ...divProps
  }) => {
    const nativeValue = value ? toNative(value) : undefined
    const nativeDefault = defaultValue ? toNative(defaultValue) : undefined
    const max = disableFuture ? toNative(dayjs()) : undefined

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      const val = ev.target.value
      if (!val) {
        onChange?.(null)
        return
      }
      onChange?.(fromNative(val))
    }

    const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
      if (!onAccept) {return}
      const val = ev.target.value
      onAccept(val ? fromNative(val) : null)
    }

    return (
      <div {...divProps} className={[styles.datePicker, divProps.className].filter(Boolean).join(' ')} style={style}>
        {label ? <label className={styles.label}>{label}</label> : null}
        <input
          ref={inputRef}
          type={type}
          className={styles.input}
          value={nativeValue}
          defaultValue={nativeDefault != null && nativeValue == null ? nativeDefault : undefined}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          max={max}
        />
      </div>
    )
  }
  return Component
}

export const DatePicker = createDatePicker(
  'date',
  d => d.format('YYYY-MM-DD'),
  s => dayjs(s)
)

export const TimePicker = createDatePicker(
  'time',
  d => d.format('HH:mm'),
  s => {
    const [h, m] = s.split(':').map(Number)
    return dayjs().hour(h).minute(m).second(0)
  }
)

export const DateTimePicker = createDatePicker(
  'datetime-local',
  d => d.format('YYYY-MM-DDTHH:mm'),
  s => dayjs(s)
)

export type DatePickerProps = BaseDatePickerProps
export type TimePickerProps = BaseDatePickerProps
export type DateTimePickerProps = BaseDatePickerProps
